import { faArchive, faEye, faFileExport, faLink, faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Loading, Modal, Pagination, TextInput } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import { GET_ITEM_LIMIT } from "constants/constants"
import {
  backendAssetToFrontend,
  CONTRACT_TYPE_LABELS,
  ContractType,
  AssetAnalysisRound,
  FrontendAsset,
  useCreateContractAssetMutation,
  useDeleteAssetMutation,
  useDownloadAssetPolygonsMutation,
  useLazyGetAssetQuery,
  useLazyListRoundsQuery,
  useListAdmin3Query,
  useListAssetCategoriesQuery,
  useListAssetSubcategoriesQuery,
  useListAssetsQuery,
  useListCountriesQuery,
  useListContractsQuery,
} from "services/aimsBackend"
import useCommon from "hooks/useCommon"
import { Moment } from "moment"
import { useEffect, useMemo, useState } from "react"
import BaseTable, { AutoResizer, ColumnShape } from "react-base-table"
import { SingleDatePicker } from "react-dates"
import { useNavigate } from "react-router-dom"
import Select from "react-select"
import { renderMonthElement } from "utils/dateHelper"
import * as xlsx from "xlsx"

type Option = { value: string; label: string }
type ExportRow = Record<string, string | number>
type BulkAssignIssue = {
  assetId: number
  label: string
  reason: string
  type: "skipped" | "failed"
}
type BulkAssignResult = {
  total: number
  succeeded: number
  skipped: number
  failed: number
  contractName: string
  contractType: string
  issues: BulkAssignIssue[]
}

const assetExportColumns = [
  "Asset_id",
  "ISO3",
  "Country",
  "FLA",
  "Admin1",
  "Admin2",
  "Community",
  "Analysis",
  "Lat_Y",
  "Long_X",
  "Geotrace",
  "AssetType",
  "AssetSubcat",
  "AssetDetail",
  "Prj_Size",
  "OutputUnit",
  "StartMonth",
  "StartYear",
  "EndMonth",
  "EndYear",
  "Villages",
  "Outcomes",
  "ImpactArea",
  "Extra_Info",
]

const roundExportColumns = [
  "RB",
  "AnalysisYR",
  "SubsYR",
  "AIMS_id",
  "Round",
  "AssetAbrev",
  "Map_id ",
  "AssetStatus",
  "Obs",
  "ImgDate",
  "Raster",
  "ImageID",
  "Year",
  "CopyRight",
  "Sensor",
  "BandCount",
  "PixRes",
]

const analysisTypeOptions: Option[] = [
  { value: "LIA", label: "LIA" },
  { value: "ADA", label: "ADA" },
  { value: "ADA/LIA", label: "ADA/LIA" },
]

const contractStatusOptions: Option[] = [
  { value: "with", label: "With contracts" },
  { value: "without", label: "Without contracts" },
]

const isDuplicateContractAssetError = (reason: unknown): boolean => {
  const data = (reason as any)?.data
  const text = typeof data === "string" ? data : JSON.stringify(data ?? {})
  return text.toLowerCase().includes("uniq_contract_asset_type") || text.toLowerCase().includes("unique")
}

const flattenApiError = (value: unknown, field?: string): string[] => {
  if (Array.isArray(value)) return value.flatMap(item => flattenApiError(item, field))
  if (value && typeof value === "object") {
    return Object.entries(value).flatMap(([key, detail]) => flattenApiError(detail, key))
  }
  if (value === undefined || value === null || value === "") return []
  return [`${field ? `${field}: ` : ""}${String(value)}`]
}

const getBulkAssignFailureReason = (reason: unknown): string => {
  const data = (reason as any)?.data
  const messages = flattenApiError(data)
  if (messages.length > 0) return messages.join("; ")
  const status = (reason as any)?.status
  return status ? `Assignment request failed (${status})` : "Assignment request failed"
}

const groupBulkAssignIssues = (issues: BulkAssignIssue[]): Array<{ reason: string; count: number }> => {
  const counts = issues.reduce<Record<string, number>>((acc, issue) => {
    acc[issue.reason] = (acc[issue.reason] ?? 0) + 1
    return acc
  }, {})
  return Object.entries(counts).map(([reason, count]) => ({ reason, count }))
}

const emptyRoundExportRow = (): ExportRow =>
  roundExportColumns.reduce<ExportRow>((row, column) => ({ ...row, [column]: "" }), {})

const assetToExportRow = (asset: FrontendAsset, countryNameByIso3: Map<string, string>): ExportRow => ({
  Asset_id: asset.assetId || "Not assigned",
  ISO3: asset.country,
  Country: countryNameByIso3.get(asset.country) ?? asset.country,
  FLA: asset.fla,
  Admin1: asset.admin1,
  Admin2: asset.admin2,
  Community: asset.community,
  Analysis: asset.analysis,
  Lat_Y: asset.latY ?? "",
  Long_X: asset.longX ?? "",
  Geotrace: asset.geotrace,
  AssetType: asset.assetType,
  AssetSubcat: asset.assetSubcat,
  AssetDetail: asset.assetDetail,
  Prj_Size: asset.prjSize || "",
  OutputUnit: asset.outputUnit,
  StartMonth: asset.startMonth,
  StartYear: asset.startYear || "",
  EndMonth: asset.endMonth,
  EndYear: asset.endYear || "",
  Villages: asset.villages,
  Outcomes: asset.outcomes,
  ImpactArea: asset.impactArea,
  Extra_Info: asset.extraInfo,
})

const roundToExportRow = (
  round: AssetAnalysisRound,
  asset: FrontendAsset,
  countryNameByIso3: Map<string, string>,
): ExportRow => ({
  RB: "",
  ISO3: asset.country,
  Country: countryNameByIso3.get(asset.country) ?? asset.country,
  Asset_id: asset.assetId || round.AssetId || "Not assigned",
  AnalysisYR: round.analysis_year ?? "",
  SubsYR: "",
  AIMS_id: round.aims_id ?? "",
  Round: round.label ?? "",
  AssetAbrev: round.asset_abbreviation ?? "",
  "Map_id ": round.Map_id ?? "",
  AssetStatus: round.status?.name ?? "",
  Obs: round.observation ?? "",
  ImgDate: round.image_date ?? "",
  Raster: round.raster ?? "",
  ImageID: round.image_id ?? "",
  Year: round.year ?? "",
  CopyRight: round.copyright ?? "",
  Sensor: round.sensor ?? "",
  BandCount: round.band_count ?? "",
  PixRes: "",
})

const writeExportWorkbook = (rows: ExportRow[], columns: string[], includeAnalysis: boolean) => {
  const worksheet = xlsx.utils.json_to_sheet(rows, { header: columns })
  worksheet["!cols"] = columns.map(column => ({ wch: Math.min(Math.max(column.length + 4, 14), 32) }))

  const workbook = xlsx.utils.book_new()
  xlsx.utils.book_append_sheet(workbook, worksheet, includeAnalysis ? "Assets Analysis" : "Assets")
  xlsx.writeFile(workbook, `aims-assets-export-${new Date().toISOString().slice(0, 10)}.xlsx`)
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export default function AssetListPage() {
  const navigate = useNavigate()
  const { showSnackBar } = useCommon()

  const { data: contractsList } = useListContractsQuery({ page_size: 50 })
  const { data: countries = [] } = useListCountriesQuery()
  const { data: admin3List = [] } = useListAdmin3Query()
  const { data: categories = [] } = useListAssetCategoriesQuery()
  const { data: subcategories = [] } = useListAssetSubcategoriesQuery()
  const [deleteAsset, { isLoading: isDeleting }] = useDeleteAssetMutation()
  const [downloadAssetPolygons] = useDownloadAssetPolygonsMutation()
  const [createContractAsset] = useCreateContractAssetMutation()
  const [fetchAsset] = useLazyGetAssetQuery()
  const [fetchRounds] = useLazyListRoundsQuery()

  // Filters
  const [search, setSearch] = useState("")
  const [countryFilter, setCountryFilter] = useState<Option[]>([])
  const [communityFilter, setCommunityFilter] = useState<Option[]>([])
  const [contractStatusFilter, setContractStatusFilter] = useState<Option | null>(null)
  const [analysisFilter, setAnalysisFilter] = useState<Option | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<Option | null>(null)
  const [subcategoryFilter, setSubcategoryFilter] = useState<Option | null>(null)
  const [dateFrom, setDateFrom] = useState<Moment | null>(null)
  const [dateTo, setDateTo] = useState<Moment | null>(null)
  const [focusedDateFrom, setFocusedDateFrom] = useState(false)
  const [focusedDateTo, setFocusedDateTo] = useState(false)

  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(GET_ITEM_LIMIT)
  const [deleteTarget, setDeleteTarget] = useState<FrontendAsset | null>(null)
  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([])
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false)
  const [bulkAssignResult, setBulkAssignResult] = useState<BulkAssignResult | null>(null)
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null)
  const [selectedContractType, setSelectedContractType] = useState<Option | null>(null)
  const [filterYears, setFilterYears] = useState<Option[]>([])
  const [filterOwner, setFilterOwner] = useState<Option | null>(null)
  const [filterType, setFilterType] = useState<Option | null>(null)
  const [isBulkAssigning, setIsBulkAssigning] = useState(false)
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false)
  const [isBulkDeleting, setIsBulkDeleting] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [includeAnalysisExport, setIncludeAnalysisExport] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const { data: backendAssets, isFetching } = useListAssetsQuery({
    page: pageNumber,
    page_size: pageSize,
    search: search.trim() || undefined,
    analysis_type: analysisFilter?.value as any,
    asset_type: categoryFilter?.value,
    asset_subcat: subcategoryFilter?.value,
    country: countryFilter.map(option => option.value),
    community: communityFilter.map(option => option.value),
    has_contracts:
      contractStatusFilter?.value === "with" ? true : contractStatusFilter?.value === "without" ? false : undefined,
    start_date_from: dateFrom?.format("YYYY-MM-DD"),
    start_date_to: dateTo?.format("YYYY-MM-DD"),
  })
  const assets = useMemo(() => (backendAssets?.results ?? []).map(backendAssetToFrontend), [backendAssets])
  const contracts = useMemo(() => contractsList?.results ?? [], [contractsList])
  const selectedCount = selectedAssetIds.length
  const selectedIdSet = useMemo(() => new Set(selectedAssetIds), [selectedAssetIds])
  const pageAssetIds = useMemo(() => assets.map(asset => asset.id), [assets])
  const pageSelectedCount = pageAssetIds.filter(id => selectedIdSet.has(id)).length
  const allPageAssetsSelected = pageAssetIds.length > 0 && pageAssetIds.every(id => selectedIdSet.has(id))

  // Derived options
  const categoryOptions = useMemo(() => categories.map(c => ({ value: c.title, label: c.title })), [categories])

  const subcategoryOptions = useMemo(() => {
    if (!categoryFilter) return subcategories.map(s => ({ value: s.title, label: s.title }))
    const cat = categories.find(c => c.title === categoryFilter.value)
    if (!cat) return []
    return subcategories.filter(s => s.category.id === cat.id).map(s => ({ value: s.title, label: s.title }))
  }, [categoryFilter, categories, subcategories])

  const countryOptions = useMemo(() => countries.map(item => ({ value: item.iso3, label: item.name })), [countries])
  const countryNameByIso3 = useMemo(() => new Map(countries.map(item => [item.iso3, item.name])), [countries])
  const communityOptions = useMemo(() => admin3List.map(item => ({ value: item.name, label: item.name })), [admin3List])
  const yearOptions = useMemo<Option[]>(() => {
    const years = new Set<number>()
    contracts.forEach(contract => years.add(contract.subscription_year))
    return Array.from(years)
      .sort((a, b) => b - a)
      .map(year => ({ value: String(year), label: String(year) }))
  }, [contracts])

  const ownerOptions = useMemo<Option[]>(() => {
    const owners = new Set<string>()
    contracts.forEach(contract => contract.owner && owners.add(contract.owner))
    return Array.from(owners)
      .sort()
      .map(owner => ({ value: owner, label: owner }))
  }, [contracts])

  const typeOptions = useMemo<Option[]>(
    () => [
      { value: ContractType.ADA, label: CONTRACT_TYPE_LABELS[ContractType.ADA] },
      { value: ContractType.LIA, label: CONTRACT_TYPE_LABELS[ContractType.LIA] },
    ],
    [],
  )

  const availableContracts = useMemo(() => {
    let filtered = contracts.filter(contract =>
      contract.contract_types.some(type => type === ContractType.ADA || type === ContractType.LIA),
    )
    if (filterYears.length > 0) {
      const years = filterYears.map(year => Number(year.value))
      filtered = filtered.filter(contract => years.includes(contract.subscription_year))
    }
    if (filterOwner) {
      filtered = filtered.filter(contract => contract.owner === filterOwner.value)
    }
    if (filterType) {
      filtered = filtered.filter(contract => contract.contract_types.includes(filterType.value as ContractType))
    }
    return filtered
  }, [contracts, filterOwner, filterType, filterYears])

  const contractSelectOptions = useMemo<Array<{ value: number; label: string }>>(
    () =>
      availableContracts.map(contract => ({
        value: contract.id,
        label: `${contract.name} (${contract.contract_types
          .map(type => CONTRACT_TYPE_LABELS[type as ContractType] ?? type)
          .join(", ")})`,
      })),
    [availableContracts],
  )

  const selectedContract = useMemo(
    () => contracts.find(contract => contract.id === selectedContractId) ?? null,
    [contracts, selectedContractId],
  )

  const attachTypeOptions = useMemo<Option[]>(() => {
    if (!selectedContract) return []
    return selectedContract.contract_types
      .filter(type => type === ContractType.ADA || type === ContractType.LIA)
      .map(type => ({
        value: type,
        label: CONTRACT_TYPE_LABELS[type as ContractType] ?? type,
      }))
  }, [selectedContract])

  const bulkAssignIssueGroups = useMemo(
    () => (bulkAssignResult ? groupBulkAssignIssues(bulkAssignResult.issues) : []),
    [bulkAssignResult],
  )

  useEffect(() => {
    setPageNumber(1)
    setSelectedAssetIds([])
  }, [
    search,
    countryFilter,
    communityFilter,
    contractStatusFilter,
    analysisFilter,
    categoryFilter,
    subcategoryFilter,
    dateFrom,
    dateTo,
    pageSize,
  ])

  // Clear subcategory when category changes
  useEffect(() => {
    setSubcategoryFilter(null)
  }, [categoryFilter])

  const resetBulkAssignModal = () => {
    setFilterYears([])
    setFilterOwner(null)
    setFilterType(null)
    setSelectedContractId(null)
    setSelectedContractType(null)
  }

  const toggleAssetSelection = (assetId: number, selected: boolean) => {
    setSelectedAssetIds(prev => {
      if (selected) return prev.includes(assetId) ? prev : [...prev, assetId]
      return prev.filter(id => id !== assetId)
    })
  }

  const togglePageSelection = (selected: boolean) => {
    setSelectedAssetIds(prev => {
      if (selected) {
        const next = new Set(prev)
        pageAssetIds.forEach(id => next.add(id))
        return Array.from(next)
      }
      return prev.filter(id => !pageAssetIds.includes(id))
    })
  }

  const getSelectedAssetLabel = (assetId: number) => {
    const asset = assets.find(item => item.id === assetId)
    return asset?.assetId || asset?.fla || `Asset ${assetId}`
  }

  const handleBulkAssign = async () => {
    if (!selectedContract || !selectedContractType || selectedAssetIds.length === 0) return

    setIsBulkAssigning(true)
    try {
      const results: PromiseSettledResult<number>[] = []
      await selectedAssetIds.reduce(
        (previous, assetId) =>
          previous.then(async () => {
            try {
              await createContractAsset({
                contract: selectedContract.id,
                asset: assetId,
                contract_type: selectedContractType.value as ContractType,
              }).unwrap()
              results.push({ status: "fulfilled", value: assetId })
            } catch (reason) {
              results.push({ status: "rejected", reason })
            }
          }),
        Promise.resolve(),
      )
      const issues: BulkAssignIssue[] = results.flatMap((result, index) => {
        if (result.status === "fulfilled") return []
        const assetId = selectedAssetIds[index]
        const isDuplicate = isDuplicateContractAssetError(result.reason)
        return [
          {
            assetId,
            label: getSelectedAssetLabel(assetId),
            reason: isDuplicate ? "already linked to this contract/type" : getBulkAssignFailureReason(result.reason),
            type: isDuplicate ? "skipped" : "failed",
          },
        ]
      })
      const succeeded = results.filter(result => result.status === "fulfilled").length
      const skipped = issues.filter(issue => issue.type === "skipped").length
      const failed = issues.filter(issue => issue.type === "failed").length

      setBulkAssignResult({
        total: results.length,
        succeeded,
        skipped,
        failed,
        contractName: selectedContract.name,
        contractType: selectedContractType.label,
        issues,
      })
      setShowBulkAssignModal(false)
      resetBulkAssignModal()

      if (succeeded === results.length) {
        setSelectedAssetIds([])
      }
    } finally {
      setIsBulkAssigning(false)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedAssetIds.length === 0) return

    setIsBulkDeleting(true)
    try {
      const results = await Promise.allSettled(selectedAssetIds.map(assetId => deleteAsset(assetId).unwrap()))
      const succeeded = results.filter(result => result.status === "fulfilled").length
      const failed = results.length - succeeded

      if (failed === 0) {
        showSnackBar(`${succeeded} asset${succeeded === 1 ? " was" : "s were"} deleted`, "success")
        setSelectedAssetIds([])
        setShowBulkDeleteModal(false)
      } else if (succeeded > 0) {
        showSnackBar(`${succeeded} asset${succeeded === 1 ? "" : "s"} deleted; ${failed} failed`, "warning")
        setSelectedAssetIds([])
        setShowBulkDeleteModal(false)
      } else {
        showSnackBar("Failed to delete selected assets", "error")
      }
    } finally {
      setIsBulkDeleting(false)
    }
  }

  const handleExportSelectedAssets = async () => {
    if (selectedAssetIds.length === 0) return

    setIsExporting(true)
    try {
      const selectedAssets = await Promise.all(
        selectedAssetIds.map(assetId => fetchAsset(assetId).unwrap().then(backendAssetToFrontend)),
      )

      let rows: ExportRow[] = selectedAssets.map(asset => assetToExportRow(asset, countryNameByIso3))
      let columns = assetExportColumns

      if (includeAnalysisExport) {
        columns = [...assetExportColumns, ...roundExportColumns]
        const roundLists = await Promise.all(
          selectedAssets.map(asset =>
            fetchRounds({ asset: asset.id, page_size: 1000 })
              .unwrap()
              .then(rounds => ({ asset, rounds: rounds.results ?? [] })),
          ),
        )
        rows = roundLists.flatMap(({ asset, rounds }) => {
          const assetRow = assetToExportRow(asset, countryNameByIso3)
          if (rounds.length === 0) return [{ ...assetRow, ...emptyRoundExportRow() }]
          return rounds.map(round => ({ ...assetRow, ...roundToExportRow(round, asset, countryNameByIso3) }))
        })
      }

      writeExportWorkbook(rows, columns, includeAnalysisExport)
      const polygonZip = await downloadAssetPolygons({ asset_ids: selectedAssetIds }).unwrap()
      const hasPolygonZip = polygonZip.size > 0
      if (hasPolygonZip) {
        downloadBlob(polygonZip, `aims-assets-polygons-${new Date().toISOString().slice(0, 10)}.zip`)
      }
      showSnackBar(
        `${selectedAssets.length} asset${selectedAssets.length === 1 ? "" : "s"} exported${
          hasPolygonZip ? " with polygon zip" : ""
        }`,
        "success",
      )
      setShowExportModal(false)
    } catch {
      showSnackBar("Failed to export selected assets", "error")
    } finally {
      setIsExporting(false)
    }
  }

  const columns: Array<ColumnShape<FrontendAsset>> = [
    {
      key: "__selection__",
      width: 48,
      flexGrow: 0,
      flexShrink: 0,
      headerRenderer: () => (
        <input
          type="checkbox"
          aria-label="Select all assets on this page"
          checked={allPageAssetsSelected}
          disabled={pageAssetIds.length === 0}
          ref={input => {
            if (input) input.indeterminate = pageSelectedCount > 0 && !allPageAssetsSelected
          }}
          onChange={event => togglePageSelection(event.target.checked)}
        />
      ),
      cellRenderer: ({ rowData }) => (
        <input
          type="checkbox"
          aria-label={`Select asset ${rowData.id}`}
          checked={selectedIdSet.has(rowData.id)}
          onChange={event => toggleAssetSelection(rowData.id, event.target.checked)}
        />
      ),
    },
    { key: "assetId", dataKey: "assetId", title: "Asset ID", width: 130, flexGrow: 1 },
    { key: "admin1", dataKey: "admin1", title: "Admin 1", width: 140, flexGrow: 1 },
    { key: "admin2", dataKey: "admin2", title: "Admin 2", width: 140, flexGrow: 1 },
    { key: "community", dataKey: "community", title: "Community", width: 160, flexGrow: 1 },
    {
      key: "start",
      title: "Start",
      width: 100,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => `${rowData.startMonth} ${rowData.startYear}`,
    },
    {
      key: "end",
      title: "End",
      width: 100,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => `${rowData.endMonth} ${rowData.endYear}`,
    },
    { key: "assetType", dataKey: "assetType", title: "Category", width: 120, flexGrow: 1 },
    {
      key: "action",
      title: "",
      width: 500,
      cellRenderer: ({ rowData }) => (
        <div className="d-flex" style={{ gap: 4 }}>
          <Button
            action={() => navigate(`/aims/AssetsView/preview/${rowData.id}`)}
            label="Details"
            className="btn-full blue action-btn"
            icon={faEye}
          />
          <Button
            action={() => navigate(`/aims/AssetsView/edit/${rowData.id}`)}
            label="Edit"
            className="btn-full blue action-btn"
            icon={faPen}
          />
          <Button
            action={() => showSnackBar("Asset archived", "success")}
            label="Archive"
            className="btn-full action-btn"
            icon={faArchive}
            style={{ color: "#8c8c8c" }}
          />
          <Button
            action={() => setDeleteTarget(rowData)}
            label="Delete"
            className="btn-full danger action-btn"
            icon={faTrash}
          />
        </div>
      ),
    },
  ]

  return (
    <>
      <h2 className="m-0 mb-3 mt-2">Assets</h2>
      <HeaderFilters>
        <Loading active={isFetching} />
        <div className="d-flex align-items-center justify-end w-full" style={{ gap: 8 }}>
          {selectedCount > 0 && (
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <span style={{ display: "flex", alignItems: "center", fontSize: 13, fontWeight: 700 }}>
                {selectedCount} selected
              </span>
              <Button
                action={() => {
                  resetBulkAssignModal()
                  setShowBulkAssignModal(true)
                }}
                label="Assign contracts"
                className="btn-full blue"
                icon={faLink}
                style={{ minWidth: 170 }}
              />
              <Button
                action={() => setShowExportModal(true)}
                label="Export"
                className="btn-full"
                icon={faFileExport}
                style={{ minWidth: 120 }}
              />
              <Button
                action={() => setShowBulkDeleteModal(true)}
                label="Delete"
                className="btn-full danger"
                icon={faTrash}
                style={{ minWidth: 120 }}
              />
            </div>
          )}
          <Button
            action={() => navigate("/aims/AssetsView/add-manual")}
            label="Add manual"
            className="btn-full green"
            icon={faPlus}
            style={{ minWidth: 140 }}
          />
          <Button
            action={() => navigate("/aims/AssetsView/add")}
            label="Add assets with XLS"
            className="btn-full"
            icon={faPlus}
            style={{ minWidth: 200 }}
          />
        </div>
      </HeaderFilters>

      {/* Filters */}
      <div className="mt-2 mb-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Search
          </p>
          <TextInput
            name="assetSearch"
            placeholder="Admin, community, category..."
            type="text"
            value={search}
            style={{ fontSize: "0.875rem", height: 37, borderRadius: 4 }}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Analysis Type
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={analysisTypeOptions}
            value={analysisFilter}
            onChange={val => setAnalysisFilter(val)}
            placeholder="Select type"
            isClearable
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Contract Status
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={contractStatusOptions}
            value={contractStatusFilter}
            onChange={val => setContractStatusFilter(val)}
            placeholder="Show assets"
            isClearable
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Country
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={countryOptions}
            isMulti
            value={countryFilter}
            onChange={val => setCountryFilter(val as Option[])}
            placeholder="Select countries"
            isClearable
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Category
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={categoryOptions}
            value={categoryFilter}
            onChange={val => setCategoryFilter(val)}
            placeholder="Select category"
            isClearable
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Subcategory
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={subcategoryOptions}
            value={subcategoryFilter}
            onChange={val => setSubcategoryFilter(val)}
            placeholder="Select subcategory"
            isClearable
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Communities
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={communityOptions}
            isMulti
            value={communityFilter}
            onChange={val => setCommunityFilter(val as Option[])}
            placeholder="Select communities"
            isClearable
          />
        </div>
        <div>
          <p className="m-0 mb-1" style={{ fontSize: 12, fontWeight: 600 }}>
            Date Range
          </p>
          <div className="d-flex" style={{ gap: 8 }}>
            <div style={{ flex: 1 }}>
              <SingleDatePicker
                id="asset-filter-from"
                date={dateFrom}
                focused={focusedDateFrom}
                onFocusChange={({ focused }) => {
                  setFocusedDateFrom(!!focused)
                  if (focused) setFocusedDateTo(false)
                }}
                onDateChange={setDateFrom}
                numberOfMonths={1}
                small
                block
                showDefaultInputIcon
                inputIconPosition="after"
                displayFormat="MM/DD/YYYY"
                placeholder="From"
                renderMonthElement={renderMonthElement}
                isOutsideRange={() => false}
              />
            </div>
            <div style={{ flex: 1 }}>
              <SingleDatePicker
                id="asset-filter-to"
                date={dateTo}
                focused={focusedDateTo}
                onFocusChange={({ focused }) => {
                  setFocusedDateTo(!!focused)
                  if (focused) setFocusedDateFrom(false)
                }}
                onDateChange={setDateTo}
                numberOfMonths={1}
                small
                block
                showDefaultInputIcon
                inputIconPosition="after"
                displayFormat="MM/DD/YYYY"
                placeholder="To"
                renderMonthElement={renderMonthElement}
                isOutsideRange={() => false}
              />
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: "100%", minHeight: "60vh", marginTop: "10px" }}>
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              key={selectedAssetIds.join(",")}
              data={assets}
              width={width}
              height={height}
              rowKey="id"
              className="custom-table"
              rowHeight={38}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p>No records to display</p>}
            />
          )}
        </AutoResizer>
      </div>

      <Pagination
        totalItems={backendAssets?.count ?? 0}
        pageSize={pageSize}
        pageSizes={[10, 20, 50]}
        page={pageNumber}
        onChange={(event: any): void => {
          const nextPageSize = (event as any).pageSize || pageSize
          setPageSize(nextPageSize)
          setPageNumber(nextPageSize === pageSize ? (event as any).page || 1 : 1)
        }}
      />

      <DeleteConfirmationModal
        open={!!deleteTarget}
        onRequestClose={() => setDeleteTarget(null)}
        onRequestSubmit={async () => {
          if (!deleteTarget) return
          try {
            await deleteAsset(deleteTarget.id).unwrap()
            setSelectedAssetIds(prev => prev.filter(id => id !== deleteTarget.id))
            showSnackBar("Asset deleted successfully", "success")
          } catch (err: any) {
            const data = err?.data
            const message =
              typeof data === "string"
                ? data
                : data && typeof data === "object"
                ? Object.entries(data)
                    .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
                    .join("; ")
                : "Failed to delete asset"
            showSnackBar(message, "error")
          } finally {
            setDeleteTarget(null)
          }
        }}
      >
        {isDeleting
          ? "Deleting..."
          : `Are you sure you want to delete "${
              deleteTarget?.fla || deleteTarget?.assetType
            }"? This will also remove its analysis rounds and contract links.`}
      </DeleteConfirmationModal>

      <DeleteConfirmationModal
        open={showBulkDeleteModal}
        onRequestClose={() => setShowBulkDeleteModal(false)}
        onRequestSubmit={handleBulkDelete}
      >
        {isBulkDeleting
          ? "Deleting..."
          : `Are you sure you want to delete ${selectedCount} selected asset${
              selectedCount === 1 ? "" : "s"
            }? This will also remove their analysis rounds and contract links.`}
      </DeleteConfirmationModal>

      <Modal
        open={showBulkAssignModal}
        modalHeading="Assign Contract to Assets"
        primaryButtonText={isBulkAssigning ? "Assigning..." : "Assign"}
        secondaryButtonText="Cancel"
        onRequestClose={() => setShowBulkAssignModal(false)}
        onSecondarySubmit={() => setShowBulkAssignModal(false)}
        onRequestSubmit={handleBulkAssign}
        primaryButtonDisabled={!selectedContractId || !selectedContractType || selectedCount === 0 || isBulkAssigning}
      >
        <Loading active={isBulkAssigning} />
        <div className="mb-3">
          <p className="m-0" style={{ fontSize: 13 }}>
            This will link the selected contract to <b>{selectedCount}</b> selected asset
            {selectedCount === 1 ? "" : "s"}. The backend will create the related ADA/LIA rounds for each successful
            link.
          </p>
          <p className="m-0 mt-2" style={{ fontSize: 13, color: "#b86200" }}>
            Only assets that share a country with the selected contract will be linked. Country mismatches will be
            listed in the results.
          </p>
          {selectedContractType?.value === ContractType.LIA && (
            <p className="m-0 mt-2" style={{ fontSize: 13, color: "#b86200" }}>
              LIA links require each selected asset to already have a validated polygon. Assets without one will be
              skipped by the backend.
            </p>
          )}
        </div>

        <div className="mb-3">
          <p className="m-0 mb-1" style={{ fontWeight: 600, fontSize: 13 }}>
            Subscription Year
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={yearOptions}
            isMulti
            value={filterYears}
            onChange={val => {
              setFilterYears(val as Option[])
              setSelectedContractId(null)
              setSelectedContractType(null)
            }}
            placeholder="Filter by year"
            isClearable
          />
        </div>

        <div className="mb-3">
          <p className="m-0 mb-1" style={{ fontWeight: 600, fontSize: 13 }}>
            Owner
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={ownerOptions}
            value={filterOwner}
            onChange={val => {
              setFilterOwner(val)
              setSelectedContractId(null)
              setSelectedContractType(null)
            }}
            placeholder="Filter by owner"
            isClearable
          />
        </div>

        <div className="mb-3">
          <p className="m-0 mb-1" style={{ fontWeight: 600, fontSize: 13 }}>
            Contract Type
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={typeOptions}
            value={filterType}
            onChange={val => {
              setFilterType(val)
              setSelectedContractId(null)
              setSelectedContractType(null)
            }}
            placeholder="Filter by type"
            isClearable
          />
        </div>

        <div className="mb-3">
          <p className="m-0 mb-1" style={{ fontWeight: 600, fontSize: 13 }}>
            Select Contract
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={contractSelectOptions}
            value={contractSelectOptions.find(option => option.value === selectedContractId) || null}
            onChange={option => {
              setSelectedContractId(option?.value ?? null)
              setSelectedContractType(null)
            }}
            placeholder="Search contracts..."
            isSearchable
            noOptionsMessage={() =>
              contracts.length === 0 ? "No contracts available to link." : "No contracts match the selected filters."
            }
          />
        </div>

        <div className="mb-3">
          <p className="m-0 mb-1" style={{ fontWeight: 600, fontSize: 13 }}>
            Attach as type
          </p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={attachTypeOptions}
            value={selectedContractType}
            onChange={val => setSelectedContractType(val)}
            placeholder={selectedContract ? "Pick the contract type for this link" : "Select a contract first"}
            isDisabled={!selectedContract}
          />
        </div>
      </Modal>

      <Modal
        open={!!bulkAssignResult}
        modalHeading="What happened?"
        primaryButtonText="Done"
        secondaryButtonText=""
        onRequestClose={() => setBulkAssignResult(null)}
        onRequestSubmit={() => setBulkAssignResult(null)}
      >
        {bulkAssignResult && (
          <div>
            <p className="m-0 mb-2" style={{ fontSize: 14 }}>
              Contract: <b>{bulkAssignResult.contractName}</b> as <b>{bulkAssignResult.contractType}</b>
            </p>
            <div
              className="mb-3"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                gap: 8,
              }}
            >
              <div style={{ padding: 10, background: "#f3f6f8", borderRadius: 4 }}>
                <p className="m-0" style={{ fontSize: 12, color: "#5a6872" }}>
                  Selected
                </p>
                <p className="m-0" style={{ fontWeight: 700, fontSize: 18 }}>
                  {bulkAssignResult.total}
                </p>
              </div>
              <div style={{ padding: 10, background: "#e8f5e9", borderRadius: 4 }}>
                <p className="m-0" style={{ fontSize: 12, color: "#2e7d32" }}>
                  Linked
                </p>
                <p className="m-0" style={{ fontWeight: 700, fontSize: 18 }}>
                  {bulkAssignResult.succeeded}
                </p>
              </div>
              <div style={{ padding: 10, background: "#fff4e0", borderRadius: 4 }}>
                <p className="m-0" style={{ fontSize: 12, color: "#b86200" }}>
                  Skipped
                </p>
                <p className="m-0" style={{ fontWeight: 700, fontSize: 18 }}>
                  {bulkAssignResult.skipped}
                </p>
              </div>
              <div style={{ padding: 10, background: "#fdecea", borderRadius: 4 }}>
                <p className="m-0" style={{ fontSize: 12, color: "#b00020" }}>
                  Failed
                </p>
                <p className="m-0" style={{ fontWeight: 700, fontSize: 18 }}>
                  {bulkAssignResult.failed}
                </p>
              </div>
            </div>

            {bulkAssignResult.issues.length === 0 ? (
              <p className="m-0" style={{ fontSize: 13 }}>
                All selected assets were linked successfully.
              </p>
            ) : (
              <>
                <p className="m-0 mb-2" style={{ fontSize: 13 }}>
                  {bulkAssignResult.succeeded} out of {bulkAssignResult.total} selected assets were linked. The assets
                  below were skipped or failed.
                </p>
                <div className="mb-3">
                  <p className="m-0 mb-1" style={{ fontWeight: 700, fontSize: 13 }}>
                    Reason summary
                  </p>
                  {bulkAssignIssueGroups.map(group => (
                    <p key={group.reason} className="m-0" style={{ fontSize: 13 }}>
                      {group.count} asset{group.count === 1 ? "" : "s"}: {group.reason}
                    </p>
                  ))}
                </div>
                <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #dde1e6", borderRadius: 4 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f3f6f8" }}>
                        <th style={{ textAlign: "left", padding: 8 }}>Asset</th>
                        <th style={{ textAlign: "left", padding: 8 }}>Result</th>
                        <th style={{ textAlign: "left", padding: 8 }}>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkAssignResult.issues.map(issue => (
                        <tr key={`${issue.assetId}-${issue.reason}`} style={{ borderTop: "1px solid #dde1e6" }}>
                          <td style={{ padding: 8 }}>{issue.label}</td>
                          <td style={{ padding: 8, textTransform: "capitalize" }}>{issue.type}</td>
                          <td style={{ padding: 8 }}>{issue.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={showExportModal}
        modalHeading="Export Selected Assets"
        primaryButtonText={isExporting ? "Exporting..." : "Export"}
        secondaryButtonText="Cancel"
        onRequestClose={() => setShowExportModal(false)}
        onSecondarySubmit={() => setShowExportModal(false)}
        onRequestSubmit={handleExportSelectedAssets}
        primaryButtonDisabled={selectedCount === 0 || isExporting}
      >
        <Loading active={isExporting} />
        <p className="m-0 mb-3" style={{ fontSize: 13 }}>
          Export <b>{selectedCount}</b> selected asset{selectedCount === 1 ? "" : "s"} to an Excel file.
        </p>
        <label className="d-flex align-items-start" style={{ gap: 8, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={includeAnalysisExport}
            onChange={event => setIncludeAnalysisExport(event.target.checked)}
          />
          <span>
            Include analysis rounds. Assets with multiple ADA/LIA rounds will be repeated once per round in the export.
          </span>
        </label>
      </Modal>
    </>
  )
}
